import type {
  CaseReportType,
  CasesSubmittedTrend,
  CasesTimeByStep,
  GetCasesAnalyticsResponse,
} from '@compass/client/types/cases_analytics';
import { CaseSlaStatus } from '@compass/client/types/cases_analytics';
import { useQuery } from '@tanstack/react-query';

import type { SupportedTimeZone } from '@/constants/date';
import { applyTrendBucketLabels, buildTrendBucketPlan } from '@/pages/analytics/utils/dateWindow';
import { casesAnalyticsService } from '@/services/api';
import { naiveDateToUtc } from '@/utils/date';
import { serializeMultiValueParam } from '@/utils/urlParams';

import { HOUR_BUCKET_STARTS } from '../utils/constants';

const casesAnalyticsQueryKey = ['compass', 'cases-analytics'] as const;

interface UseCasesAnalyticsArgs {
  organizationId: string | null;
  programIds?: string[];
  reportTypes?: CaseReportType[];
  slaStatus?: CaseSlaStatus;
  start?: string;
  end?: string;
  timeZone: SupportedTimeZone;
}

const SUBMITTED_TREND_KEYS = ['by_report_type', 'by_program', 'by_submission_method'] as const;

/** Rewrites each trend row's `date` (the bucket's UTC start, as sent) to its local YYYY-MM-DD. */
const relabel = (
  response: GetCasesAnalyticsResponse,
  labelByStart: Record<string, string>,
): GetCasesAnalyticsResponse => {
  const sla = applyTrendBucketLabels(response, ['sla_compliance_trend'], labelByStart);
  const submitted = applyTrendBucketLabels<CasesSubmittedTrend>(
    response.submitted_trend ?? {},
    SUBMITTED_TREND_KEYS,
    labelByStart,
  );
  const byStep = applyTrendBucketLabels<CasesTimeByStep>(
    response.time_by_step ?? {},
    ['trend'],
    labelByStart,
  );
  return { ...sla, submitted_trend: submitted, time_by_step: byStep };
};

const buildParams = (
  { organizationId, programIds, reportTypes, slaStatus, start, timeZone }: UseCasesAnalyticsArgs,
  trendPlan: ReturnType<typeof buildTrendBucketPlan>,
) => ({
  organization_id: organizationId ?? '',
  program_ids: serializeMultiValueParam(programIds),
  report_types: serializeMultiValueParam(reportTypes?.map(String)),
  sla_status: slaStatus === CaseSlaStatus.CASE_SLA_STATUS_UNSPECIFIED ? undefined : slaStatus,
  start: start ? naiveDateToUtc(start, timeZone, 'start') : undefined,
  // [start, end): the next local midnight after the last selected day.
  end: trendPlan?.rangeEndExclusive,
  ...trendPlan?.params,
  time_to_submit_bucket_starts_hours: [...HOUR_BUCKET_STARTS],
});

export const useCasesAnalytics = (args: UseCasesAnalyticsArgs) => {
  const { organizationId, start, end, timeZone } = args;
  const trendPlan = buildTrendBucketPlan(start, end, timeZone);
  const params = buildParams(args, trendPlan);
  const { organization_id, ...queryParams } = params;

  const query = useQuery({
    queryKey: [...casesAnalyticsQueryKey, params] as const,
    queryFn: async () => {
      // program_ids and report_types go on the wire comma-joined (as QC's program_ids), which
      // the generated request type (string[]) does not describe, so params are passed through.
      const response = await casesAnalyticsService.getCasesAnalytics(
        { organization_id },
        { params: queryParams },
      );
      return trendPlan ? relabel(response, trendPlan.labelByStart) : response;
    },
    // start/end are set after the first render on a bare URL; without them the backend rejects
    // the request (they are required).
    enabled: !!organizationId && !!start && !!end,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  const labelByStart = trendPlan?.labelByStart ?? {};
  const partialStarts = trendPlan?.partialStarts ?? new Set<string>();
  // The same buckets by their chart label, since the data is relabelled by the time it is drawn.
  const partialDates = new Set([...partialStarts].map(s => labelByStart[s]));

  // Axis and tooltip dates follow the grain the buckets were built with, not `trend_bucket`.
  const trendBucket = trendPlan?.bucket ?? 'day';

  return { ...query, labelByStart, partialStarts, partialDates, trendBucket };
};
